import React from 'react';
import { Sidebar } from './Sidebar';
import { Outlet, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { Logo } from './Brand';
import {
  LayoutDashboard, Dog, Calendar, ShoppingCart, Users, BookOpen, MessagesSquare,
  Menu, LogOut, UserCircle, Heart, GraduationCap, MessageCircle, FileBarChart, PenSquare, LineChart, Share2,
  BookOpenCheck,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';

const clientMobileLinks = [
  { to: '/client', label: 'Dashboard', icon: LayoutDashboard, testid: 'mobile-client-dashboard' },
  { to: '/client/book', label: 'Book', icon: Calendar, testid: 'mobile-client-book' },
  { to: '/client/dogs', label: 'Dogs', icon: Dog, testid: 'mobile-client-dogs' },
  { to: '/client/bookings', label: 'Bookings', icon: BookOpen, testid: 'mobile-client-bookings' },
  { to: '/community', label: 'Community', icon: MessagesSquare, testid: 'mobile-community' },
];

const adminMobileLinks = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, testid: 'mobile-admin-dashboard' },
  { to: '/admin/run-grid', label: 'Grid', icon: Calendar, testid: 'mobile-admin-grid' },
  { to: '/admin/clients', label: 'Clients', icon: Users, testid: 'mobile-admin-clients' },
  { to: '/admin/pos', label: 'POS', icon: ShoppingCart, testid: 'mobile-admin-pos' },
  { to: '/admin/team-chat', label: 'Team', icon: MessageCircle, testid: 'mobile-admin-team-chat' },
];

const fullSheetLinksByVariant = {
  client: [
    { to: '/client', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/client/dogs', label: 'My Dogs', icon: Dog },
    { to: '/client/book', label: 'Book Boarding', icon: Calendar },
    { to: '/client/bookings', label: 'My Bookings', icon: BookOpen },
    { to: '/client/courses', label: 'My Courses', icon: BookOpenCheck },
    { to: '/client/messages', label: 'Messages', icon: MessageCircle },
    { to: '/client/profile', label: 'My Profile', icon: UserCircle },
    { to: '/client/membership', label: 'Memberships', icon: Heart },
    { to: '/community', label: 'Community', icon: MessagesSquare },
  ],
  admin: [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/admin/run-grid', label: 'Run Grid', icon: Calendar },
    { to: '/admin/trainer-grid', label: 'Trainer Grid', icon: GraduationCap },
    { to: '/admin/training', label: 'Training Bookings', icon: GraduationCap },
    { to: '/admin/bookings', label: 'Boarding', icon: BookOpen },
    { to: '/admin/clients', label: 'Clients & Dogs', icon: Users },
    { to: '/admin/pos', label: 'POS Terminal', icon: ShoppingCart },
    { to: '/admin/messages', label: 'Client Messages', icon: MessageCircle },
    { to: '/admin/team-chat', label: 'Team Chat', icon: MessageCircle },
    { to: '/admin/reports', label: 'Reports', icon: FileBarChart },
    { to: '/admin/analytics', label: 'Live Analytics', icon: LineChart },
    { to: '/admin/social', label: 'Social Hub', icon: Share2 },
    { to: '/admin/blog', label: 'Blog', icon: PenSquare },
    { to: '/community', label: 'Community', icon: MessagesSquare },
  ],
  trainer: [
    { to: '/admin', label: 'My Dogs', icon: Dog },
    { to: '/admin/training', label: 'My Training', icon: GraduationCap },
    { to: '/admin/trainer-grid', label: 'Trainer Grid', icon: Calendar },
    { to: '/admin/clients', label: 'Clients & Dogs', icon: Users },
    { to: '/admin/messages', label: 'Client Messages', icon: MessageCircle },
    { to: '/admin/team-chat', label: 'Team Chat', icon: MessageCircle },
    { to: '/community', label: 'Community', icon: MessagesSquare },
  ],
};

const MobileTopBar = ({ variant }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fullLinks = fullSheetLinksByVariant[variant] || fullSheetLinksByVariant.client;
  return (
    <header
      className='jfk-mobile-topbar lg:hidden sticky top-0 z-30 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 h-14 flex items-center justify-between px-3'
      style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(56px + env(safe-area-inset-top))' }}
    >
      <Sheet>
        <SheetTrigger asChild>
          <button
            className='h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200'
            data-testid='portal-mobile-menu-trigger'
            aria-label='Open menu'
          >
            <Menu className='w-4 h-4' />
          </button>
        </SheetTrigger>
        <SheetContent side='left' className='w-[260px] dark:bg-slate-900 dark:text-slate-100'>
          <div className='mt-2 mb-4'><Logo /></div>
          <div className='flex flex-col gap-1'>
            {fullLinks.map((l) => (
              <NavLink
                key={l.to + l.label}
                to={l.to}
                end={l.to === '/client' || l.to === '/admin'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive ? 'bg-[#1B3A87] text-white' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`
                }
              >
                <l.icon className='w-4 h-4' /> {l.label}
              </NavLink>
            ))}
            <div className='border-t border-slate-200 dark:border-slate-700 my-3' />
            <button
              className='flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md'
              onClick={() => { logout(); navigate('/'); }}
            >
              <LogOut className='w-4 h-4' /> Sign out
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <div className='flex items-center gap-2'>
        <Logo size={28} showText={false} />
        <div className='leading-tight'>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>{user?.role}</div>
          <div className='text-xs font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[120px]'>{user?.full_name}</div>
        </div>
      </div>
      <ThemeToggle size='sm' />
    </header>
  );
};

const MobileBottomBar = ({ variant }) => {
  const links = variant === 'client' ? clientMobileLinks : adminMobileLinks;
  return (
    <nav
      className='lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 grid grid-cols-5'
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 6px)' }}
      data-testid='mobile-bottom-nav'
    >
      {links.map((l) => (
        <NavLink
          key={l.to + l.label}
          to={l.to}
          end={l.to === '/client' || l.to === '/admin'}
          className={({ isActive }) =>
            `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold tracking-wider ${
              isActive ? 'text-[#1B3A87] dark:text-[#93C5FD]' : 'text-slate-500 dark:text-slate-400'
            }`
          }
          data-testid={l.testid}
        >
          <l.icon className='w-4 h-4' />
          <span>{l.label}</span>
        </NavLink>
      ))}
    </nav>
  );
};

export const ClientLayout = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className='p-10 text-slate-500'>Loading…</div>;
  if (!user) return <Navigate to='/login' replace />;
  if (user.role !== 'CLIENT') return <Navigate to='/admin' replace />;
  return (
    <div className='jfk-portal-bg min-h-screen flex bg-[#FFF8F2] dark:bg-slate-950'>
      <Sidebar variant='client' />
      <main className='flex-1 min-w-0 flex flex-col pb-[calc(64px+env(safe-area-inset-bottom))] lg:pb-0'>
        <MobileTopBar variant='client' />
        <div className='flex-1 min-w-0'>
          <Outlet />
        </div>
        <MobileBottomBar variant='client' />
      </main>
    </div>
  );
};

export const AdminLayout = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className='p-10 text-slate-500'>Loading…</div>;
  if (!user) return <Navigate to='/login' replace />;
  if (user.role === 'CLIENT') return <Navigate to='/client' replace />;
  const variant = user.role === 'TRAINER' ? 'trainer' : 'admin';
  return (
    <div className='jfk-portal-bg min-h-screen flex bg-[#FFF8F2] dark:bg-slate-950 text-slate-900 dark:text-slate-100'>
      <Sidebar variant={variant} />
      <main className='flex-1 min-w-0 flex flex-col pb-[calc(64px+env(safe-area-inset-bottom))] lg:pb-0'>
        <MobileTopBar variant={variant} />
        <div className='flex-1 min-w-0'>
          <Outlet />
        </div>
        <MobileBottomBar variant={variant === 'trainer' ? 'admin' : variant} />
      </main>
    </div>
  );
};
